import { z } from "zod";
import type { UIMessage } from "ai";

const optionalText = z.string().nullable().optional();
const fact = z.object({
  label: z.string(),
  value: z.string(),
  sourceId: optionalText,
});
const perspective = z.object({
  id: z.string(),
  title: z.string(),
  rows: z.array(
    z.object({
      label: z.string(),
      values: z.array(
        z.object({
          entityId: z.number().int(),
          value: z.string(),
          unit: optionalText,
          sourceId: optionalText,
          missing: z.boolean(),
        })
      ),
    })
  ),
  caveat: optionalText,
  recommendation: z
    .object({
      entityId: z.number().int().nullable().optional(),
      label: z.string(),
      reason: z.string(),
      sourceIds: z.array(z.string()),
    })
    .nullable()
    .optional(),
});
const comparisonEvidence = z.object({ entityId: z.number().int(), perspective: z.string(), attribute: z.string(), value: z.string() });
const assessment = z.object({
  conclusions: z.array(z.object({ perspective: z.string(), conclusion: z.string(), evidence: z.array(comparisonEvidence), caveats: z.array(z.string()) })).length(4),
  recommendation: z.object({ kind: z.enum(["preferred", "assortment", "conditional", "no_clear_winner"]), productIds: z.array(z.number().int()), rationale: z.string(), alternatives: z.string(), confirmBeforeProceeding: z.array(z.string()), evidence: z.array(comparisonEvidence), sellingAdvice: optionalText }),
});
const brief = z.object({ mandatory: z.array(z.string()), preferences: z.array(z.string()), unresolved: z.array(z.string()), provenance: z.string() }).nullable().optional();
export const productSchema = z.object({
  entityId: z.number().int().positive(),
  name: z.string(),
  itemNumber: optionalText,
  brand: optionalText,
  category: optionalText,
  description: optionalText,
  imageUrl: optionalText,
  sourceId: optionalText,
  evaluation: z.array(perspective).length(4).optional(),
  matchReasons: z.array(fact).optional(),
});
export const widgetSchema = z.discriminatedUnion("component", [
  z.object({
    component: z.literal("ProductResultList"),
    title: optionalText,
    products: z.array(productSchema).max(10),
    brief,
    pagination: z.object({ resultSetId: z.string().uuid(), page: z.number().int().positive(), pageSize: z.literal(10), availableCount: z.number().int().nonnegative(), ranked: z.boolean(), retrievalMode: z.string(), fallbackReason: optionalText, unverified: z.boolean() }).optional(),
  }),
  z.object({
    component: z.literal("ProductComparison"),
    title: optionalText,
    products: z.array(productSchema).min(2).max(3),
    perspectives: z.array(perspective).min(3).max(4),
    assessment: assessment.nullable().optional(),
    assessmentId: optionalText,
  }),
  z.object({
    component: z.literal("AttributeHighlights"),
    title: optionalText,
    attributes: z.array(fact),
  }),
  z.object({
    component: z.literal("ClarificationQuestion"),
    question: z.string(),
    options: z.array(z.string()).min(1).max(6),
  }),
  z.object({
    component: z.literal("FollowUpSuggestions"),
    suggestions: z.array(z.string()).min(1).max(6),
  }),
  z.object({
    component: z.literal("SourceReferences"),
    sources: z.array(fact),
  }),
  z.object({
    component: z.literal("ClassicSearchFallback"),
    message: z.string(),
    query: z.string(),
  }),
]);
export type Product = z.infer<typeof productSchema>;
const internalTextMarkers = [
  "Conversation widgets and selections:",
  "Saved product context for this turn:",
  "```json",
  '{"type":"data-widget"',
  '{"toolResults":',
];

export function assistantText(text: string, streaming = false) {
  for (const marker of internalTextMarkers) {
    const index = text.toLowerCase().indexOf(marker.toLowerCase());
    if (index >= 0) text = text.slice(0, index);
    // Withhold a partially streamed internal label so it cannot flash in the transcript.
    if (streaming) {
      for (let length = marker.length - 1; length > 0; length--) {
        if (
          text.toLowerCase().endsWith(marker.slice(0, length).toLowerCase())
        ) {
          text = text.slice(0, -length);
          break;
        }
      }
    }
  }
  return text;
}
export type Widget = z.infer<typeof widgetSchema>;
export type ProductWidget = Extract<
  Widget,
  { component: "ProductResultList" | "ProductComparison" }
>;
export function productResults(messages: AssistantMessage[]) {
  return messages.flatMap((message) =>
    message.role !== "assistant"
      ? []
      : message.parts.flatMap((part, index) => {
          if (part.type !== "data-widget") return [];
          const parsed = widgetSchema.safeParse(part.data);
          if (
            !parsed.success ||
            !(
              parsed.data.component === "ProductResultList" ||
              parsed.data.component === "ProductComparison"
            )
          )
            return [];
          return [
            {
              key: `${message.id}:${index}`,
              messageId: message.id,
              widget: parsed.data,
              complete: message.metadata?.status === "complete",
            },
          ];
        })
  );
}
export type AssistantMessage = UIMessage<
  { status: string; createdAt?: string },
  { widget: Widget; selection: Product[] }
>;
export interface ConversationSummary {
  id: string;
  title: string;
  updatedAt: string;
}
export interface Conversation extends ConversationSummary {
  messages: AssistantMessage[];
  generating: boolean;
}
export const conversationPath = "/products/assistant/conversations";
export function conversationUrl(id: string) {
  return `/?view=article-assistant-chat&conversation=${encodeURIComponent(id)}`;
}
export function safeImage(value?: string | null) {
  return value && /^https?:\/\//i.test(value) ? value : undefined;
}
